import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { content } from '@/content/content';
import { OG_IMAGE_URL, SITE_URL } from '@/config/site';

interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string;
    ogImage?: string;
    ogType?: string;
    canonical?: string;
}

const SEO = ({
    title = content.seo.default.title,
    description = content.seo.default.description,
    keywords = content.seo.default.keywords,
    ogImage = OG_IMAGE_URL,
    ogType = 'website',
    canonical,
}: SEOProps): null => {
    const location = useLocation();
    const currentUrl = canonical || `${SITE_URL}${location.pathname}`;

    useEffect(() => {
        // Update document title
        document.title = title;

        // Update or create meta tags
        const updateMetaTag = (name: string, content: string, isProperty = false) => {
            const attribute = isProperty ? 'property' : 'name';
            let element = document.querySelector(`meta[${attribute}="${name}"]`);

            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attribute, name);
                document.head.appendChild(element);
            }

            element.setAttribute('content', content);
        };

        // Primary meta tags
        updateMetaTag('description', description);
        updateMetaTag('keywords', keywords);

        // Open Graph tags
        updateMetaTag('og:title', title, true);
        updateMetaTag('og:description', description, true);
        updateMetaTag('og:url', currentUrl, true);
        updateMetaTag('og:image', ogImage, true);
        updateMetaTag('og:type', ogType, true);

        // Twitter Card tags
        updateMetaTag('twitter:title', title);
        updateMetaTag('twitter:description', description);
        updateMetaTag('twitter:image', ogImage);
        updateMetaTag('twitter:url', currentUrl);

        // Update canonical link
        let canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (!canonicalLink) {
            canonicalLink = document.createElement('link');
            canonicalLink.setAttribute('rel', 'canonical');
            document.head.appendChild(canonicalLink);
        }
        canonicalLink.setAttribute('href', currentUrl);
    }, [title, description, keywords, ogImage, ogType, currentUrl]);

    return null;
};

export default SEO;
